import React, { Component } from "react";
import { PropTypes } from "prop-types";
import styled from "styled-components";
import * as Immutable from "immutable";

import {
  CCard,
  CFontIcon,
  Box,
  Flex,
  H3,
  Caption
} from "@appcues/component-library";
import PublishStatusTag from "components/Common/PublishStatusTag";
import ThreeDotsDropdownButton from "components/Common/ThreeDotsDropdownButton";
import StepTagControl from "components/StepIndex/StepView/StepListItem/StepTagControl";

import {
  getAllTimeStatsForStep,
  getFlowActionsDropdownItems,
  getTagsDropdownItems
} from "helpers/view";
import { getContentTypeFromStep } from "helpers/steps";

export class StepListItem extends Component {
  static propTypes = {
    className: PropTypes.string,
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string,
    step: PropTypes.object,
    stepTags: PropTypes.instanceOf(Immutable.Set).isRequired,
    stepAnalytics: PropTypes.instanceOf(Immutable.Map).isRequired,
    tags: PropTypes.instanceOf(Immutable.Map).isRequired,
    targetedSegmentName: PropTypes.string,
    onSelectDropdownOption: PropTypes.func,
    onClickFlowItem: PropTypes.func,
    renderItemIcon: PropTypes.func.isRequired
  };

  shouldComponentUpdate(nextProps) {
    if (!Immutable.is(nextProps.step, this.props.step)) {
      return true;
    }

    if (!Immutable.is(nextProps.stepTags, this.props.stepTags)) {
      return true;
    }

    if (!Immutable.is(nextProps.tags, this.props.tags)) {
      return true;
    }

    if (
      !Immutable.is(
        nextProps.targetedSegmentName,
        this.props.targetedSegmentName
      )
    ) {
      return true;
    }

    if (!Immutable.is(nextProps.stepAnalytics, this.props.stepAnalytics)) {
      return true;
    }

    if (
      !Immutable.is(
        nextProps.onSelectDropdownOption,
        this.props.onSelectDropdownOption
      )
    ) {
      return true;
    }

    if (!Immutable.is(nextProps.onClick, this.props.onClick)) {
      return true;
    }

    return false;
  }

  onClickListItem = e => {
    e.stopPropagation();
    const { step, onClickFlowItem } = this.props;
    onClickFlowItem(step);
  };

  render() {
    const {
      className,
      createdAt,
      updatedAt,
      step,
      stepTags,
      stepAnalytics,
      tags,
      targetedSegmentName,
      onSelectDropdownOption,
      renderItemIcon
    } = this.props;

    const analytics = getAllTimeStatsForStep(stepAnalytics.toObject());
    const stepId = step.id;
    const stepState = step.state;

    const tagsAsObject = tags.toObject();
    const stepTagsAsArray = stepTags.toArray();

    const tagsDropdownItems = getTagsDropdownItems(tagsAsObject);
    const dropdownOptions = getFlowActionsDropdownItems(
      getContentTypeFromStep(step),
      stepId,
      stepState
    );

    return (
      <StyledStepListItem
        className={`${className} step-list-item`}
        onClick={this.onClickListItem}
      >
        <StyledStepImageContainer>
          {renderItemIcon({
            step,
            stepImageHeight: "100%"
          })}
        </StyledStepImageContainer>
        <StyledStepContentContainer flexDirection="column">
          <H3 title={step.name} type="secondary" paddingY={1} truncate>
            {step.name}
          </H3>
          <Caption is="div" type="secondary" marginTop={7} truncate>
            Created {createdAt}
            {updatedAt && ` | Updated ${updatedAt}`}
          </Caption>
          <StepTagControl
            stepId={stepId}
            step={step}
            stepTags={stepTagsAsArray}
            tags={tagsAsObject}
            tagsDropdownItems={tagsDropdownItems}
            marginTop={5}
            flexGrow={1}
          />
        </StyledStepContentContainer>
        <StyledStepActionsContainer flexDirection="column">
          <Flex justifyContent="flex-end" marginRight={-8}>
            <PublishStatusTag published={step.published} state={step.state} />
            <ThreeDotsDropdownButton
              className="option-button step-actions-dropdown"
              handleOnSelect={onSelectDropdownOption}
              dropdownOptions={dropdownOptions}
            />
          </Flex>
          {analytics && (
            <Flex justifyContent="flex-end" marginTop={8}>
              <Caption type="secondary" is="div" truncate>
                {analytics.reached} users shown | {analytics.completed}{" "}
                completed
              </Caption>
              <StyledOrbIcon>
                <CFontIcon className="fas fa-chart-bar" />
              </StyledOrbIcon>
            </Flex>
          )}
          {targetedSegmentName && (
            <Flex justifyContent="flex-end" marginTop={8}>
              <Caption is="div" type="secondary" truncate>
                {targetedSegmentName}
              </Caption>
              <StyledOrbIcon>
                <CFontIcon className="fas fa-users" />
              </StyledOrbIcon>
            </Flex>
          )}
        </StyledStepActionsContainer>
      </StyledStepListItem>
    );
  }
}

const StyledOrbIcon = styled(Box)`
  margin-left: 8px;
  background-color: ${props => props.theme["$gray-2"]};
  color: ${props => props.theme["$gray-5"]};
  font-size: 0.625rem;
  height: 1.5rem;
  min-width: 1.5rem;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledStepListItem = styled(CCard)`
  height: calc(100% - 16px);
  margin-right: 16px;
  display: flex;

  &:hover {
    .add-tag-container {
      opacity: 1;
    }
  }
`;

const StyledStepContentContainer = styled(Flex)`
  flex: 1;
  padding: 16px 32px 15px 24px;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  flex-flow: row wrap;
  min-width: 0;
`;

const StyledStepActionsContainer = styled(Flex)`
  flex: 2;
  min-width: 0;
  max-width: 30%;
  padding: 16px 20px 15px 0px;
`;

const StyledStepImageContainer = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  width: 218px;
  min-height: 100%;
  background-color: ${props => props.theme["$gray-3"]};
  padding: 8px 15px;
  border-radius: 6px 0 0 6px;

  img {
    max-width: 100%;
    max-height: 100%;
  }
`;

export default StepListItem;
